// Play submenu: Free play (starts a dealt hand vs. three bots) and Lessons.
// Lessons routes to the LessonList screen (registered under the `pre-game`
// scene value — see main.ts). The list is empty until T12 authors content.

import type { MenuScreen, MahjongMenuContext } from '../types.js';
import type { Variant } from '../../settings/types.js';
import { createScreenShell, createMenuHeader, createCrestButton, el } from '@bn-games/ui-shell';
import { isDiscordActivity } from '@bn-games/discord-activity';
import { t } from '../../i18n/I18n.js';

/**
 * Visible variant picker — mirrors the Settings screen's `variant` row (same
 * `updateSettings` persistence path) but surfaced here so the choice that
 * `startFreePlay()` will use (wired in T17) is visible right where the user
 * starts a game, not only buried in Settings. Two `createCrestButton`s reuse
 * the existing `primary` flag as the "selected" visual state — no new CSS.
 */
function buildVariantPicker(ctx: MahjongMenuContext): HTMLElement {
  const container = el('div', { class: 'button-row' });

  function render(): void {
    while (container.firstChild) container.removeChild(container.firstChild);
    const current = ctx.getSettings().variant;
    const options: readonly Variant[] = ['simplified', 'classical'];
    options.forEach((variant) => {
      container.appendChild(
        createCrestButton({
          label: t(`settings.variants.${variant}`),
          small: true,
          primary: variant === current,
          onClick: () => {
            if (variant === current) return;
            ctx.updateSettings({ variant });
            render();
          },
        }),
      );
    });
  }

  render();
  return container;
}

export function createPlayMenuScreen(): MenuScreen {
  let frame: HTMLElement | null = null;

  return {
    mount(root, ctx) {
      const shell = createScreenShell();
      frame = shell.frame;
      const body = shell.body;
      body.style.justifyContent = 'space-between';

      body.appendChild(createMenuHeader({ title: t('menu.play') }));
      body.appendChild(buildVariantPicker(ctx));

      // Inside Discord the instance room is the only online room that exists —
      // the activity joined it on startup, and a code typed into the iframe
      // could only ever pull the player out of their own channel's table. Free
      // play and the lessons stay reachable, so a solo launcher still plays.
      const roomCodeLobby = !isDiscordActivity();
      const savedOnline = roomCodeLobby ? ctx.online.getSavedSession() : null;

      const buttons = el('div', { class: 'button-stack' }, [
        // A rejoinable online table takes over the primary slot from Free
        // Play — free play always starts fresh, so it never has its own
        // resume entry (mahjong has no free-play active-session recorder).
        savedOnline
          ? createCrestButton({
              label: t('menu.resumeOnline', { code: savedOnline.roomCode }),
              primary: true,
              onClick: () => {
                void ctx.online.resumeRoom(savedOnline.roomCode);
              },
            })
          : null,
        createCrestButton({
          label: t('menu.freePlay'),
          primary: !savedOnline,
          onClick: () => ctx.startFreePlay(),
        }),
        createCrestButton({
          label: t('menu.lessons'),
          onClick: () => ctx.navigate('pre-game'),
        }),
        // The variant picker above seeds the online form's ruleset, so it
        // applies to a table you host as well as to free play.
        roomCodeLobby
          ? createCrestButton({
              label: t('menu.playOnline'),
              onClick: () => ctx.navigate('online-create'),
            })
          : null,
        roomCodeLobby
          ? createCrestButton({
              label: t('menu.joinOnline'),
              onClick: () => ctx.navigate('online-join'),
            })
          : null,
      ]);

      const back = el('div', { class: 'button-row back-button-row' }, [
        createCrestButton({
          label: t('common.back'),
          small: true,
          onClick: () => ctx.navigate('menu'),
        }),
      ]);

      const center = el('div', { class: 'about-center' }, [buttons]);
      body.appendChild(center);
      body.appendChild(back);
      root.appendChild(frame);
    },
    unmount() {
      frame?.remove();
      frame = null;
    },
  };
}
